<template>
  <Component
    :is="type"
    class="streem-modal2-mask"
    :class="{
      isMobile: $isMobile,
      'fill-height': fullHeight,
      'menu-offset': hasMenuOffset,
      'sidebar-offset': sideBarOpen && $isDesktop,
    }"
    form-class="tw:h-full tw:w-full"
    :style="{ '--sidebar-width': sideBarWidth }"
    @click="clickOutside"
    @submit="emit('submit', $event)"
  >
    <div
      class="stream-modal2-container"
      :class="containerClass"
      :style="modalWidth"
    >
      <div
        ref="modalContainer"
        :aria-label="ariaLabel"
        :aria-labelledby="hasHeader ? headerId : undefined"
        aria-modal="true"
        class="streem-modal2 tw:flex tw:flex-col tw:flex-nowrap"
        :class="modalClass"
        data-testid="core-modal"
        role="dialog"
        :style="modalHeight"
        tabindex="-1"
        @click.stop
        @keydown.esc="closeOnEscape"
      >
        <div class="relative">
          <BaseButton
            v-if="!hideCloseButton"
            :aria-label="$t('global.close')"
            circle
            class="streem-modal2-close"
            :class="{
              'streem-modal2-close__with-header': hasHeader,
            }"
            :color="closeButtonColor"
            data-testid="close-button"
            flat
            icon="close"
            new-design
            new-icon
            @click="tryClose"
          />

          <div
            v-if="hasHeader"
            :id="headerId"
            ref="headerContainer"
            class="streem-modal2-header"
            :class="headerClass"
          >
            <slot name="header">
              <div
                aria-level="2"
                class="streem-modal2-header-title"
                data-testid="modal-title"
                role="heading"
              >
                {{ title }}
              </div>
            </slot>
          </div>
        </div>

        <BaseLoadingBar
          v-if="loading"
          data-testid="modal-loading-bar"
          height="2"
        />

        <div
          ref="contentContainer"
          class="streem-modal2-content"
          :class="contentClass"
          :style="contentStyle"
        >
          <slot />
        </div>
        <div
          v-if="hasButtonsSlot"
          ref="buttonsContainer"
          class="streem-modal2-buttons"
          :class="buttonsClass"
        >
          <slot
            :close="tryClose"
            name="buttons"
          />
        </div>
      </div>
    </div>
  </Component>
</template>

<script setup lang="ts">
import { useElementSize } from "@vueuse/core";
import { useFocusTrap } from "@vueuse/integrations/useFocusTrap";
import { storeToRefs } from "pinia";
import type { CSSProperties } from "vue";
import {
  computed,
  getCurrentInstance,
  inject,
  reactive,
  useId,
  useSlots,
  useTemplateRef,
} from "vue";

import {
  BaseButton,
  BaseFormWrapper,
  BaseLoadingBar,
} from "shared/components/base";
import useModal from "shared/composables/useModal";
import { useSideBarStore } from "shared/modules/SideBar/stores/useSideBarStore";
import { useGlobalStore } from "shared/stores/global";

import { type ModalProps } from "./types";

const props = withDefaults(defineProps<ModalProps>(), {
  buttonsClass: "streem-modal2-buttons__layout",
  closeButtonColor: undefined,
  containerClass: "",
  contentClass: "tw:p-4",
  contentStyle: () => ({}),
  form: false,
  fullHeight: false,
  headerClass: "tw:p-4 tw:pr-12",
  hideCloseButton: false,
  loading: false,
  modalClass: "",
  modalStyle: () => ({}),
  offsetMenu: false,
  persistent: false,
  title: "",
});

interface Emits {
  (e: "before-close", cancel: () => void): void;
  (e: "close"): void;
  (e: "submit", event: Event): void;
}

const emit = defineEmits<Emits>();

const $isMobile = inject<boolean>("isMobile");
const $isDesktop = inject<boolean>("isDesktop");

const sideBarStore = useSideBarStore();
const { sideBarOpen, sideBarWidth } = storeToRefs(sideBarStore);

const { isTheMenuOpen } = storeToRefs(useGlobalStore());

const slots = useSlots();
const instance = getCurrentInstance();
const modalName = instance?.parent?.type.name || "";
const { close } = useModal(modalName);

const headerId = useId();

const modalContainer = useTemplateRef<HTMLDivElement>("modalContainer");
const headerContainer = useTemplateRef<HTMLDivElement>("headerContainer");
const contentContainer = useTemplateRef<HTMLDivElement>("contentContainer");
const buttonsContainer = useTemplateRef<HTMLDivElement>("buttonsContainer");

useFocusTrap(modalContainer, {
  allowOutsideClick: true,
  escapeDeactivates: false,
  fallbackFocus: () => modalContainer.value!,
  immediate: true,
});

const contentBorderBox = useElementSize(contentContainer, undefined, {
  box: "border-box",
});

const headerBorderBox = useElementSize(headerContainer, undefined, {
  box: "border-box",
});

const buttonsBorderBox = useElementSize(buttonsContainer, undefined, {
  box: "border-box",
});

const contentContentBox = useElementSize(contentContainer, undefined, {
  box: "content-box",
});

const contentVerticalPadding = computed<number>(
  () => contentBorderBox.height.value - contentContentBox.height.value
);

const dimensions = reactive({
  header: headerBorderBox,
  content: contentBorderBox,
  contentVerticalPadding,
  buttons: buttonsBorderBox,
});

const type = computed(() => (props.form ? BaseFormWrapper : "div"));

const hasButtonsSlot = computed(() => Boolean(slots.buttons));

const hasHeader = computed<boolean>(() => Boolean(slots.header || props.title));

// Mobile overlays the menu, and on desktop it collapses to zero width when
// narrow (e.g. 400% zoom) — offsetting either way pads the dialog off-screen.
const hasMenuOffset = computed<boolean>(
  () => props.offsetMenu && Boolean($isDesktop) && isTheMenuOpen.value
);

const modalWidth = computed(() => {
  const styles: CSSProperties = {};

  if (props.modalStyle.maxWidth) {
    styles.maxWidth = props.modalStyle.maxWidth;
  } else if (props.modalStyle.width) {
    styles.width = props.modalStyle.width;
  }

  if (props.modalStyle.minWidth) {
    styles.minWidth = props.modalStyle.minWidth;
  }

  return styles;
});

const modalHeight = computed<CSSProperties>(() => {
  const styles: CSSProperties = {};

  if (props.modalStyle.height) {
    styles.height = props.modalStyle.height;
  } else if (props.modalStyle.maxHeight) {
    styles.maxHeight = props.modalStyle.maxHeight;
  }

  if (props.modalStyle.minHeight) {
    styles.minHeight = props.modalStyle.minHeight;
  }

  return styles;
});

const clickOutside = () => {
  if (!props.persistent) {
    tryClose();
  }
};

const tryClose = () => {
  let hasCanceled = false;

  const cancel = () => {
    hasCanceled = true;
  };

  emit("before-close", cancel);
  if (hasCanceled) return;

  emit("close");
  close();
};

// Escape mirrors the header X: only dismissible modals (those showing the
// close button) close on Escape, and it routes through tryClose so
// before-close guards still run. Because focus is trapped inside the modal,
// this handler only fires for the top-most modal in a stack.
const closeOnEscape = () => {
  if (props.hideCloseButton) return;
  tryClose();
};

defineExpose({
  close,
  dimensions,
});
</script>

<style lang="scss" scoped>
.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .streem-modal2,
.modal-leave-to .streem-modal2 {
  transform: scale(1.1);
}

.streem-modal2-mask {
  // Exposed so a modal passing a fixed `modalStyle.height` can cap itself
  // against the viewport without duplicating the gutter.
  --modal-gutter: #{$modal-gutter};

  position: fixed;
  inset: 0;
  transition:
    opacity 0.3s ease,
    padding-right 0.3s ease;
  z-index: 4000;
  padding: $modal-gutter;
  overflow: hidden auto;

  &.menu-offset {
    padding-left: calc(#{$streem-menu-side-bar-width} + #{$modal-gutter});
  }

  &.sidebar-offset {
    padding-right: calc(var(--sidebar-width) + 10px);

    .stream-modal2-container {
      transform: translateX(calc((100vw - var(--sidebar-width) - 526px) / 2));
    }
  }

  .stream-modal2-container {
    display: flex;
    align-items: center;
    width: 100%;
    max-width: 500px;
    min-height: calc(100% - #{$modal-gutter} * 2);
    position: relative;
    margin: 0 auto;
    transition: transform 0.3s ease;
  }

  .streem-modal2 {
    position: relative;
    width: 100%;
    border-radius: 5px;
    background-color: white;
    box-shadow: 0 3px 10px 0 rgba(23 23 23 / 30%);
    z-index: 7500;
    transition: all 0.3s ease;

    &-close {
      position: absolute;
      z-index: 1;
      right: 8px;
      top: 8px;

      &__with-header {
        top: 50%;
        margin-top: -16px;
      }
    }

    &-header {
      border-bottom: 1px solid $hover-background;

      &-title {
        font-size: 16px;
        font-weight: bold;
        line-height: normal;
      }
    }

    &-buttons {
      border-top: 1px solid $hover-background;

      &__layout {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        padding: var(--s-spacing-md);
        gap: var(--s-spacing-sm);
      }
    }
  }

  &.fill-height {
    overflow: hidden;

    .stream-modal2-container {
      min-height: auto;
      height: 100%;
      display: block;
      padding-top: env(safe-area-inset-top);
      padding-bottom: env(safe-area-inset-bottom);

      .streem-modal2 {
        height: 100%;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        margin-top: 0;

        .streem-modal2-content {
          flex: 1;
          overflow: auto;
        }
      }
    }
  }

  &.isMobile {
    .stream-modal2-container {
      width: 100% !important;
    }
  }
}
</style>
